import {
  Document, Packer, Paragraph, TextRun, Table, TableRow, TableCell,
  Header, Footer, AlignmentType, HeadingLevel, BorderStyle, WidthType, ShadingType,
  VerticalAlign, PageNumber, LevelFormat, TabStopType, TabStopPosition
} from 'docx'
import { saveAs } from 'file-saver'

const CW = 10080
const PAGE = {
  size: { width: 12240, height: 15840 },
  margin: { top: 1080, right: 1080, bottom: 1080, left: 1080 }
}

function strip(c) { return (c || '').replace(/^#/, '') }

// Horizontal-only borders (no vertical lines)
const hBd = (color = 'E2E8F0') => ({
  top: { style: BorderStyle.SINGLE, size: 1, color },
  bottom: { style: BorderStyle.SINGLE, size: 1, color },
  left: { style: BorderStyle.NONE, size: 0, color: 'FFFFFF' },
  right: { style: BorderStyle.NONE, size: 0, color: 'FFFFFF' }
})
const noBd = () => {
  const s = { style: BorderStyle.NONE, size: 0, color: 'FFFFFF' }
  return { top: s, bottom: s, left: s, right: s }
}
const hRule = (color, size = 6) => new Paragraph({
  border: { bottom: { style: BorderStyle.SINGLE, size, color, space: 1 } },
  spacing: { before: 0, after: 0 }
})
const gap = (b = 120) => new Paragraph({ spacing: { before: b, after: 0 }, children: [] })

// Citation parsing
const FW = ['NIST CSF', 'NIST SP 800-53', 'NIST 800-53', 'NIST SP 800-171', 'NIST 800-171', 'ISO 27001', 'ISO/IEC 27001', 'CIS Control', 'CIS Controls', 'SOC 2', 'HIPAA', 'PCI DSS', 'GLBA', 'FFIEC', 'CMMC', 'GDPR', 'CCPA', 'NYDFS', 'FERPA', 'DORA', 'CARF', 'FTC Safeguards', 'SEC Cyber']

function splitCites(text) {
  if (!text) return [{ t: text, c: false }]
  const parts = []
  let rem = text, n = 0
  while (rem && n < 200) {
    n++
    const oi = rem.indexOf('(')
    if (oi === -1) { parts.push({ t: rem, c: false }); break }
    let d = 0, ci = -1
    for (let i = oi; i < rem.length; i++) {
      if (rem[i] === '(') d++
      if (rem[i] === ')') { d--; if (d === 0) { ci = i; break } }
    }
    if (ci === -1) { parts.push({ t: rem, c: false }); break }
    const end = rem[ci + 1] === '.' ? ci + 2 : ci + 1
    if (oi > 0) parts.push({ t: rem.slice(0, oi), c: false })
    const p = rem.slice(oi, end)
    parts.push({ t: p, c: FW.some(f => p.toLowerCase().includes(f.toLowerCase())) })
    rem = rem.slice(end)
  }
  return parts.length ? parts : [{ t: text, c: false }]
}


export async function exportPolicyAsDocx(
  clientPolicy, policyVersions, clientData, orgBrandConfig, orgName
) {
  const P = strip(orgBrandConfig?.primary_color) || '012438'
  const A = strip(orgBrandConfig?.accent_color) || '3D8F8F'
  const F = orgBrandConfig?.font_preference || 'Calibri'
  const firm = orgBrandConfig?.firm_display_name || orgName || 'Sidewalk Security Advisors'
  const yr = new Date().getFullYear()
  const tmk = `\u00A9 ${yr} Sidewalk Security Advisors. Prepared for ${clientData?.name || firm}.`
  const sc = clientPolicy?.structured_content || {}
  const dc = sc.document_control || {}

  // 6pt = 120 twips (half-points * 20)
  const SP6 = 120

  const txt = (t, o = {}) => new TextRun({ text: t || '', font: F, size: 21, color: '2D3748', ...o })

  const bodyP = (text) => {
    const parts = splitCites(text)
    return new Paragraph({
      spacing: { before: SP6, after: SP6 },
      children: parts.map(p => p.c
        ? txt(p.t, { size: 18, color: '64748B', italics: true })
        : txt(p.t)
      )
    })
  }

  const secHead = (num, title) => new Paragraph({
    heading: HeadingLevel.HEADING_1,
    spacing: { before: 280, after: SP6 },
    border: { bottom: { style: BorderStyle.SINGLE, size: 4, color: A, space: 4 } },
    keepNext: true,
    keepLines: true,
    children: [
      txt(`${num}. `, { size: 26, bold: true, color: P }),
      txt(title.toUpperCase(), { size: 26, bold: true, color: P })
    ]
  })

  // H2: 12pt before, 6pt after
  const subSec = (id, title) => new Paragraph({
    heading: HeadingLevel.HEADING_2,
    spacing: { before: 240, after: SP6 },
    keepNext: true,
    keepLines: true,
    children: [
      txt(`${id}  `, { size: 22, bold: true, color: P }),
      txt(title, { size: 22, bold: true, color: P })
    ]
  })

  // Table cells — no vertical borders
  const hCell = (t, w) => new TableCell({
    width: { size: w, type: WidthType.DXA },
    shading: { fill: P, type: ShadingType.CLEAR },
    borders: hBd(P),
    margins: { top: 50, bottom: 50, left: 80, right: 60 },
    children: [new Paragraph({ children: [txt(t, { size: 17, bold: true, color: 'FFFFFF' })] })]
  })

  const dCell = (t, w, alt = false, o = {}) => new TableCell({
    width: { size: w, type: WidthType.DXA },
    shading: { fill: alt ? 'F8FAFC' : 'FFFFFF', type: ShadingType.CLEAR },
    borders: hBd('E2E8F0'),
    margins: { top: 50, bottom: 50, left: 80, right: 60 },
    children: [new Paragraph({ children: [txt(t, { size: 19, ...o })] })]
  })

  // ── Fallback ──
  if (!clientPolicy?.structured_content || Object.keys(sc).length === 0) {
    const raw = (clientPolicy?.content || '').split(/\n\n+/).filter(Boolean).map(p => bodyP(p))
    const doc = new Document({
      styles: { default: { document: { run: { font: F, size: 21 } } } },
      sections: [{ properties: { page: PAGE }, children: [
        new Paragraph({ children: [txt(clientPolicy?.title || 'Policy', { size: 36, bold: true, color: P })] }),
        gap(100), ...raw
      ]}]
    })
    saveAs(await Packer.toBlob(doc), `${(clientData?.name || 'Policy').replace(/[^a-zA-Z0-9]/g, '_')}.docx`)
    return
  }

  const ch = []

  // ═══ COVER HEADER ═══
  ch.push(new Table({
    width: { size: CW, type: WidthType.DXA },
    columnWidths: [CW],
    rows: [new TableRow({ cantSplit: true, children: [new TableCell({
      width: { size: CW, type: WidthType.DXA },
      shading: { fill: P, type: ShadingType.CLEAR },
      borders: noBd(),
      margins: { top: 240, bottom: 240, left: 280, right: 280 },
      children: [
        new Paragraph({ spacing: { after: 120 }, children: [txt(firm.toUpperCase(), { size: 18, bold: true, color: A })] }),
        new Paragraph({ spacing: { after: 40 }, children: [txt((clientPolicy.title || clientPolicy.domain || 'Policy').toUpperCase(), { size: 40, bold: true, color: 'FFFFFF' })] }),
        new Paragraph({ children: [txt(clientPolicy.domain || '', { size: 20, color: 'C8DCF0' })] })
      ]
    })] })]
  }))
  ch.push(hRule(A, 10))
  ch.push(gap(60))

  // ═══ DOCUMENT CONTROL ═══
  ch.push(new Paragraph({ spacing: { before: 0, after: SP6 }, children: [txt('DOCUMENT CONTROL', { size: 16, bold: true, color: '64748B' })] }))
  const colW = Math.floor(CW / 4)
  const dcData = [
    [['DOCUMENT ID', dc.policy_id || 'SSA-POL-XXX-001'], ['VERSION', dc.version || clientPolicy.version_number || '1.0'], ['STATUS', clientPolicy.status === 'published' ? 'Published' : 'Draft'], ['CLASSIFICATION', dc.classification || 'Confidential']],
    [['EFFECTIVE DATE', dc.effective_date || '[EFFECTIVE DATE]'], ['REVIEW CYCLE', dc.review_cycle || 'Annual'], ['POLICY OWNER', dc.owner || 'CISO'], ['APPROVED BY', dc.approved_by || '[APPROVAL AUTHORITY]']]
  ]
  dcData.forEach(row => {
    ch.push(new Table({
      width: { size: CW, type: WidthType.DXA },
      columnWidths: [colW, colW, colW, CW - colW * 3],
      rows: [new TableRow({ cantSplit: true, children: row.map(([label, value], ci) => new TableCell({
        width: { size: ci < 3 ? colW : CW - colW * 3, type: WidthType.DXA },
        shading: { fill: 'F8FAFC', type: ShadingType.CLEAR },
        borders: hBd('E2E8F0'),
        margins: { top: 50, bottom: 50, left: 80, right: 60 },
        children: [
          new Paragraph({ children: [txt(label, { size: 13, bold: true, color: '64748B' })] }),
          new Paragraph({ spacing: { before: 20 }, children: [txt(value, { size: 19, color: P })] })
        ]
      })) })]
    }))
  })
  ch.push(hRule('E2E8F0', 4))

  // Regulatory alignment (no border)
  if ((clientPolicy.framework_tags || []).length > 0) {
    ch.push(gap(60))
    ch.push(new Paragraph({
      spacing: { before: 0, after: 0 },
      children: [
        txt('REGULATORY ALIGNMENT:  ', { size: 16, bold: true, color: '64748B' }),
        ...(clientPolicy.framework_tags || []).map((tag, i, arr) =>
          txt(i < arr.length - 1 ? `${tag}  \u00B7  ` : tag, { size: 16, bold: true, color: P })
        )
      ]
    }))
    ch.push(hRule('E2E8F0', 4))
  }

  // ═══ SECTIONS 1-9 ═══
  if (sc.purpose) { ch.push(secHead('1', 'Purpose')); ch.push(bodyP(sc.purpose)) }

  if (sc.scope) {
    ch.push(secHead('2', 'Scope'))
    if (sc.scope.applies_to) {
      const apParts = splitCites(sc.scope.applies_to)
      ch.push(new Paragraph({
        spacing: { before: SP6, after: SP6 },
        children: [
          txt('2.1 Applicability. ', { bold: true }),
          ...apParts.map(p => p.c ? txt(p.t, { size: 18, color: '64748B', italics: true }) : txt(p.t))
        ]
      }))
    }
    if (sc.scope.exclusions) {
      const exParts = splitCites(sc.scope.exclusions)
      ch.push(new Paragraph({
        spacing: { before: SP6, after: SP6 },
        children: [
          txt('2.2 Exclusions. ', { bold: true }),
          ...exParts.map(p => p.c ? txt(p.t, { size: 18, color: '64748B', italics: true }) : txt(p.t))
        ]
      }))
    }
  }

  if (sc.definitions?.length > 0) {
    ch.push(secHead('3', 'Terms & Definitions'))
    ch.push(new Table({ width: { size: CW, type: WidthType.DXA }, columnWidths: [2400, 7680], rows: [
      new TableRow({ cantSplit: true, children: [hCell('Term', 2400), hCell('Definition', 7680)] }),
      ...sc.definitions.map((d, i) => new TableRow({ cantSplit: true, children: [
        dCell(d.term, 2400, i % 2 === 0, { bold: true, color: P }),
        dCell(d.definition, 7680, i % 2 === 0)
      ]}))
    ]}))
  }

  // Policy Statements with native multi-level numbering
  if (sc.policy_statements?.length > 0) {
    ch.push(secHead('4', 'Policy Statements'))
    sc.policy_statements.forEach((ps, i) => {
      ch.push(subSec(`4.${i + 1}`, ps.title || ''))
      if (ps.content) {
        const parts = splitCites(ps.content)
        ch.push(new Paragraph({
          spacing: { before: SP6, after: SP6 },
          indent: { left: 360 },
          children: parts.map(p => p.c ? txt(p.t, { size: 18, color: '64748B', italics: true }) : txt(p.t))
        }))
      }
      ;(ps.bullets || []).forEach((b, j) => {
        const bText = typeof b === 'string' ? b : (b.text || '')
        const parts = splitCites(bText)
        ch.push(new Paragraph({
          numbering: { reference: `stmt-${i}`, level: 0 },
          spacing: { before: SP6, after: SP6 },
          children: parts.map(p => p.c ? txt(p.t, { size: 18, color: '64748B', italics: true }) : txt(p.t, { size: 20 }))
        }))
      })
    })
  }

  if (sc.roles_and_responsibilities?.length > 0) {
    ch.push(secHead('5', 'Roles & Responsibilities'))
    ch.push(new Table({ width: { size: CW, type: WidthType.DXA }, columnWidths: [2600, 7480], rows: [
      new TableRow({ cantSplit: true, children: [hCell('Role', 2600), hCell('Responsibilities', 7480)] }),
      ...sc.roles_and_responsibilities.map((r, i) => new TableRow({ cantSplit: true, children: [
        dCell(r.role, 2600, i % 2 === 0, { bold: true, color: P }),
        dCell(r.responsibilities, 7480, i % 2 === 0)
      ]}))
    ]}))
  }

  if (sc.compliance_and_enforcement) { ch.push(secHead('6', 'Compliance & Enforcement')); ch.push(bodyP(sc.compliance_and_enforcement)) }

  ch.push(secHead('7', 'Exceptions & Waivers'))
  ch.push(bodyP('Exceptions to this policy must be formally documented, approved by the Policy Owner or designee, and reviewed at each policy review cycle. Exception requests must include: a description of the non-compliance, business justification, risk assessment, compensating controls, and a proposed expiration date. All approved exceptions are logged in the organization\'s risk register.'))

  if (sc.related_documents?.length > 0) {
    ch.push(secHead('8', 'Related Policies & Referenced Standards'))
    ch.push(new Table({ width: { size: CW, type: WidthType.DXA }, columnWidths: [600, 9480], rows: [
      new TableRow({ cantSplit: true, children: [hCell('#', 600), hCell('Document / Standard', 9480)] }),
      ...sc.related_documents.map((d, i) => new TableRow({ cantSplit: true, children: [
        dCell(`${i + 1}`, 600, i % 2 === 0, { bold: true, color: '64748B' }),
        dCell(typeof d === 'string' ? d : (d.title || d), 9480, i % 2 === 0)
      ]}))
    ]}))
  }

  ch.push(secHead('9', 'Revision History'))
  const vers = policyVersions?.length > 0 ? policyVersions : [
    { version_number: '1.0', created_at: new Date().toISOString(), created_by_name: 'SSA Advisory Team', change_summary: 'Initial policy issuance' }
  ]
  const vW = [1000, 1600, 2200, 5280]
  ch.push(new Table({ width: { size: CW, type: WidthType.DXA }, columnWidths: vW, rows: [
    new TableRow({ cantSplit: true, children: [hCell('Version', vW[0]), hCell('Date', vW[1]), hCell('Author', vW[2]), hCell('Description', vW[3])] }),
    ...vers.map((v, i) => new TableRow({ cantSplit: true, children: [
      dCell(v.version_number || '1.0', vW[0], i % 2 === 0),
      dCell(v.created_at ? new Date(v.created_at).toLocaleDateString() : '', vW[1], i % 2 === 0),
      dCell(v.created_by_name || 'SSA Advisory Team', vW[2], i % 2 === 0),
      dCell(v.change_summary || 'Initial policy issuance', vW[3], i % 2 === 0)
    ]}))
  ]}))

  // ═══ NUMBERING CONFIG (native multi-level lists for each policy statement subsection) ═══
  const numberingConfig = (sc.policy_statements || []).map((ps, i) => ({
    reference: `stmt-${i}`,
    levels: [{
      level: 0,
      format: LevelFormat.DECIMAL,
      text: `4.${i + 1}.%1`,
      alignment: AlignmentType.LEFT,
      start: 1,
      style: { paragraph: { indent: { left: 900, hanging: 540 } }, run: { bold: true, color: P, size: 19, font: F } }
    }]
  }))

  // ═══ HEADER / FOOTER ═══
  const header = new Header({ children: [new Table({
    width: { size: CW, type: WidthType.DXA }, columnWidths: [6500, 3580],
    rows: [new TableRow({ cantSplit: true, children: [
      new TableCell({ width: { size: 6500, type: WidthType.DXA }, borders: { ...noBd(), bottom: { style: BorderStyle.SINGLE, size: 3, color: A } }, verticalAlign: VerticalAlign.BOTTOM, margins: { bottom: 50 },
        children: [new Paragraph({ children: [txt(firm, { size: 16, bold: true, color: P }), txt(`  \u00B7  ${clientPolicy.title || ''}`, { size: 15, color: '64748B' })] })] }),
      new TableCell({ width: { size: 3580, type: WidthType.DXA }, borders: { ...noBd(), bottom: { style: BorderStyle.SINGLE, size: 3, color: A } }, verticalAlign: VerticalAlign.BOTTOM, margins: { bottom: 50 },
        children: [new Paragraph({ alignment: AlignmentType.RIGHT, children: [txt(`${dc.policy_id || ''}  \u00B7  v${clientPolicy.version_number || clientPolicy.current_version || '1.0'}`, { size: 15, color: '64748B' })] })] })
    ] })]
  })] })

  const footer = new Footer({ children: [new Paragraph({
    border: { top: { style: BorderStyle.SINGLE, size: 3, color: A, space: 4 } },
    tabStops: [{ type: TabStopType.CENTER, position: TabStopPosition.MAX / 2 }, { type: TabStopType.RIGHT, position: TabStopPosition.MAX }],
    spacing: { before: 50 },
    children: [txt(tmk, { size: 14, color: '64748B' }), txt('\t', {}), txt(dc.classification || 'Confidential', { size: 14, color: '64748B' }), txt('\t', {}), txt('Page ', { size: 14, color: '64748B' }), new TextRun({ children: [PageNumber.CURRENT], font: F, size: 14, color: P })]
  })] })

  // ═══ ASSEMBLE ═══
  const doc = new Document({
    numbering: { config: numberingConfig },
    styles: {
      default: { document: { run: { font: F, size: 21 } } },
      paragraphStyles: [
        { id: 'Heading1', name: 'Heading 1', basedOn: 'Normal', next: 'Normal', quickFormat: true, run: { size: 26, bold: true, font: F, color: P }, paragraph: { spacing: { before: 280, after: SP6 } } },
        { id: 'Heading2', name: 'Heading 2', basedOn: 'Normal', next: 'Normal', quickFormat: true, run: { size: 22, bold: true, font: F, color: P }, paragraph: { spacing: { before: 240, after: SP6 } } }
      ]
    },
    sections: [{ properties: { page: PAGE }, headers: { default: header }, footers: { default: footer }, children: ch }]
  })

  const blob = await Packer.toBlob(doc)
  const today = new Date().toISOString().split('T')[0]
  const sn = (clientData?.name || 'SSA').replace(/[^a-zA-Z0-9]/g, '_')
  const st = (clientPolicy?.title || 'Policy').replace(/[^a-zA-Z0-9]/g, '_')
  const sv = clientPolicy?.policy_version_number || clientPolicy?.version_number || clientPolicy?.current_version || '1.0'
  saveAs(blob, `${sn}_${st}_v${sv}_${today}.docx`)
}


// ============================================================
// Structured Policy → PDF (native jsPDF, matches DOCX layout)
// ============================================================

export async function exportStructuredPolicyToPdf(clientPolicy, policyVersions, clientData, orgBrandConfig, orgName) {
  const { default: jsPDF } = await import('jspdf')

  const sc = clientPolicy?.structured_content || {}
  const dc = sc.document_control || {}
  const firm = orgBrandConfig?.firm_display_name || orgName || 'Sidewalk Security Advisors'
  const title = clientPolicy?.title || clientPolicy?.domain || 'Policy'
  const domain = clientPolicy?.domain || ''

  const pdf = new jsPDF('p', 'mm', 'letter')
  const PW = pdf.internal.pageSize.getWidth()   // 215.9
  const PH = pdf.internal.pageSize.getHeight()  // 279.4
  const ML = 18  // left margin
  const MR = 18  // right margin
  const CW = PW - ML - MR  // content width

  let y = 0 // current y position

  const NAVY = [1, 36, 56]
  const ACCENT = [61, 143, 143]
  const BODY_RGB = [45, 55, 72]
  const MUTED_RGB = [100, 116, 139]
  const WHITE = [255, 255, 255]

  const checkPage = (needed = 20) => {
    if (y + needed > PH - 18) {
      // Footer before page break
      pdf.setFontSize(7)
      pdf.setTextColor(...MUTED_RGB)
      pdf.text(`${dc.policy_id || ''} · v${dc.version || clientPolicy?.version_number || '1.0'}`, PW - MR, PH - 10, { align: 'right' })
      pdf.text(`© ${new Date().getFullYear()} Sidewalk Security Advisors`, ML, PH - 10)
      pdf.addPage()
      y = 18
    }
  }

  // ═══ COVER HEADER ═══
  pdf.setFillColor(...NAVY)
  pdf.rect(0, 0, PW, 48, 'F')

  // Accent rule + firm name
  pdf.setFillColor(...ACCENT)
  pdf.rect(ML, 14, 10, 0.7, 'F')
  pdf.setFontSize(7)
  pdf.setTextColor(...ACCENT)
  pdf.setFont('helvetica', 'bold')
  pdf.text(firm.toUpperCase(), ML + 14, 15.5)

  // Policy title
  pdf.setFontSize(18)
  pdf.setTextColor(...WHITE)
  pdf.setFont('helvetica', 'bold')
  pdf.text(title, ML, 28, { maxWidth: CW })

  // Domain
  pdf.setFontSize(9)
  pdf.setTextColor(200, 220, 240)
  pdf.setFont('helvetica', 'normal')
  pdf.text(domain, ML, 36)

  // Accent bar
  pdf.setFillColor(...ACCENT)
  pdf.rect(ML, 42, 20, 1, 'F')

  y = 56

  // ═══ DOCUMENT CONTROL ═══
  pdf.setFontSize(7)
  pdf.setTextColor(...MUTED_RGB)
  pdf.setFont('helvetica', 'bold')
  pdf.text('DOCUMENT CONTROL', ML, y)
  y += 5

  const dcFields = [
    ['Document ID', dc.policy_id || 'SSA-POL-XXX-001'],
    ['Version', dc.version || clientPolicy?.version_number || '1.0'],
    ['Status', clientPolicy?.status === 'published' ? 'Published' : 'Draft'],
    ['Classification', dc.classification || 'Confidential'],
    ['Effective Date', dc.effective_date || '[EFFECTIVE DATE]'],
    ['Review Cycle', dc.review_cycle || 'Annual'],
    ['Policy Owner', dc.owner || 'CISO'],
    ['Approved By', dc.approved_by || '[APPROVAL AUTHORITY]']
  ]

  const colW = CW / 4
  dcFields.forEach(([label, value], i) => {
    const col = i % 4
    const row = Math.floor(i / 4)
    const x = ML + col * colW
    const rowY = y + row * 12

    pdf.setFillColor(248, 250, 252)
    pdf.rect(x, rowY - 3, colW - 1, 11, 'F')

    pdf.setFontSize(5.5)
    pdf.setTextColor(...MUTED_RGB)
    pdf.setFont('helvetica', 'bold')
    pdf.text(label.toUpperCase(), x + 3, rowY + 1)

    pdf.setFontSize(8)
    pdf.setTextColor(...NAVY)
    pdf.setFont('helvetica', 'normal')
    pdf.text(value, x + 3, rowY + 6)
  })
  y += 28

  // Regulatory alignment
  if ((clientPolicy?.framework_tags || []).length > 0) {
    pdf.setFontSize(5.5)
    pdf.setTextColor(...MUTED_RGB)
    pdf.setFont('helvetica', 'bold')
    pdf.text('REGULATORY ALIGNMENT', ML, y)
    y += 4
    pdf.setFontSize(7)
    pdf.setTextColor(...NAVY)
    pdf.setFont('helvetica', 'bold')
    pdf.text((clientPolicy.framework_tags || []).join('  ·  '), ML, y)
    y += 6
  }

  // Divider
  pdf.setDrawColor(226, 232, 240)
  pdf.setLineWidth(0.3)
  pdf.line(ML, y, PW - MR, y)
  y += 8

  // ═══ HELPER FUNCTIONS ═══
  const sectionHead = (num, titleText) => {
    checkPage(20)
    y += 6
    pdf.setFontSize(11)
    pdf.setTextColor(...NAVY)
    pdf.setFont('helvetica', 'bold')
    pdf.text(`${num}.  ${titleText.toUpperCase()}`, ML, y)
    y += 2
    pdf.setDrawColor(...ACCENT)
    pdf.setLineWidth(0.5)
    pdf.line(ML, y, PW - MR, y)
    y += 6
  }

  const bodyText = (text, indent = 0) => {
    if (!text) return
    checkPage(10)
    pdf.setFontSize(8.5)
    pdf.setTextColor(...BODY_RGB)
    pdf.setFont('helvetica', 'normal')
    const lines = pdf.splitTextToSize(text, CW - indent)
    lines.forEach(line => {
      checkPage(5)
      pdf.text(line, ML + indent, y)
      y += 4.2
    })
    y += 2
  }

  const boldPrefixText = (prefix, text, indent = 0) => {
    if (!text) return
    checkPage(10)
    pdf.setFontSize(8.5)
    pdf.setFont('helvetica', 'bold')
    pdf.setTextColor(...BODY_RGB)
    const prefixW = pdf.getTextWidth(prefix)
    pdf.text(prefix, ML + indent, y)
    pdf.setFont('helvetica', 'normal')
    const lines = pdf.splitTextToSize(text, CW - indent - prefixW)
    lines.forEach((line, i) => {
      checkPage(5)
      if (i === 0) {
        pdf.text(line, ML + indent + prefixW, y)
      } else {
        pdf.text(line, ML + indent, y)
      }
      y += 4.2
    })
    y += 2
  }

  // Table helper
  const drawTable = (headers, rows, colWidths) => {
    checkPage(15)
    const totalW = colWidths.reduce((a, b) => a + b, 0)

    // Header row
    let x = ML
    pdf.setFillColor(...NAVY)
    pdf.rect(x, y - 3.5, totalW, 6, 'F')
    pdf.setFontSize(5.5)
    pdf.setTextColor(...WHITE)
    pdf.setFont('helvetica', 'bold')
    headers.forEach((h, i) => {
      pdf.text(h.toUpperCase(), x + 2, y)
      x += colWidths[i]
    })
    y += 5

    // Data rows
    rows.forEach((row, ri) => {
      checkPage(8)
      x = ML
      if (ri % 2 === 0) {
        pdf.setFillColor(248, 250, 252)
        pdf.rect(x, y - 3, totalW, 6.5, 'F')
      }
      pdf.setFontSize(7.5)
      pdf.setTextColor(...BODY_RGB)
      row.forEach((cell, ci) => {
        const isBoldCol = ci === 0 && headers[0] !== '#'
        pdf.setFont('helvetica', isBoldCol ? 'bold' : 'normal')
        if (isBoldCol) pdf.setTextColor(...NAVY)
        else pdf.setTextColor(...BODY_RGB)
        const cellText = pdf.splitTextToSize(cell || '', colWidths[ci] - 4)
        pdf.text(cellText[0] || '', x + 2, y)
        x += colWidths[ci]
      })
      y += 5.5
    })
    y += 4
  }

  // ═══ 1. PURPOSE ═══
  if (sc.purpose) {
    sectionHead('1', 'Purpose')
    bodyText(sc.purpose)
  }

  // ═══ 2. SCOPE ═══
  if (sc.scope) {
    sectionHead('2', 'Scope')
    if (sc.scope.applies_to) boldPrefixText('2.1 Applicability. ', sc.scope.applies_to)
    if (sc.scope.exclusions) boldPrefixText('2.2 Exclusions. ', sc.scope.exclusions)
  }

  // ═══ 3. DEFINITIONS ═══
  if (sc.definitions?.length > 0) {
    sectionHead('3', 'Terms & Definitions')
    drawTable(
      ['Term', 'Definition'],
      sc.definitions.map(d => [d.term, d.definition]),
      [CW * 0.24, CW * 0.76]
    )
  }

  // ═══ 4. POLICY STATEMENTS ═══
  if (sc.policy_statements?.length > 0) {
    sectionHead('4', 'Policy Statements')
    sc.policy_statements.forEach((ps, i) => {
      checkPage(12)
      // Section heading: 4.X Title
      pdf.setFontSize(9)
      pdf.setTextColor(...NAVY)
      pdf.setFont('helvetica', 'bold')
      pdf.text(`4.${i + 1}  ${ps.title}`, ML, y)
      y += 5

      // Content
      if (ps.content) bodyText(ps.content, 6)

      // Bullets
      if (ps.bullets?.length > 0) {
        ps.bullets.forEach((b, j) => {
          checkPage(8)
          const num = `4.${i + 1}.${j + 1}`
          pdf.setFontSize(8.5)
          pdf.setFont('helvetica', 'bold')
          pdf.setTextColor(...NAVY)
          pdf.text(num, ML + 8, y)
          const numW = pdf.getTextWidth(num) + 3
          pdf.setFont('helvetica', 'normal')
          pdf.setTextColor(...BODY_RGB)
          const lines = pdf.splitTextToSize(b, CW - 8 - numW)
          lines.forEach((line, li) => {
            checkPage(5)
            pdf.text(line, ML + 8 + (li === 0 ? numW : 0), y)
            y += 4.2
          })
          y += 1.5
        })
      }
      y += 2
    })
  }

  // ═══ 5. ROLES & RESPONSIBILITIES ═══
  if (sc.roles_and_responsibilities?.length > 0) {
    sectionHead('5', 'Roles & Responsibilities')
    drawTable(
      ['Role', 'Responsibilities'],
      sc.roles_and_responsibilities.map(r => [r.role, r.responsibilities]),
      [CW * 0.26, CW * 0.74]
    )
  }

  // ═══ 6. COMPLIANCE & ENFORCEMENT ═══
  if (sc.compliance_and_enforcement) {
    sectionHead('6', 'Compliance & Enforcement')
    bodyText(sc.compliance_and_enforcement)
  }

  // ═══ 7. EXCEPTIONS & WAIVERS ═══
  sectionHead('7', 'Exceptions & Waivers')
  bodyText('Exceptions to this policy must be formally documented, approved by the Policy Owner or designee, and reviewed at each policy review cycle. Exception requests must include: a description of the non-compliance, business justification, risk assessment, compensating controls, and a proposed expiration date. All approved exceptions are logged in the organization\'s risk register.')

  // ═══ 8. RELATED POLICIES ═══
  if (sc.related_documents?.length > 0) {
    sectionHead('8', 'Related Policies & Referenced Standards')
    drawTable(
      ['#', 'Document / Standard'],
      sc.related_documents.map((d, i) => [String(i + 1), d]),
      [CW * 0.06, CW * 0.94]
    )
  }

  // ═══ 9. REVISION HISTORY ═══
  sectionHead('9', 'Revision History')
  const revRows = (policyVersions || []).length > 0
    ? policyVersions.map(v => [
        v.version_number || '1.0',
        v.created_at ? new Date(v.created_at).toLocaleDateString() : '—',
        v.created_by_name || 'SSA Advisory Team',
        v.change_summary || 'Initial policy issuance'
      ])
    : [['1.0', clientPolicy?.created_at ? new Date(clientPolicy.created_at).toLocaleDateString() : '—', 'SSA Advisory Team', 'Initial policy issuance']]
  drawTable(
    ['Version', 'Date', 'Author', 'Description'],
    revRows,
    [CW * 0.1, CW * 0.12, CW * 0.2, CW * 0.58]
  )

  // ═══ FOOTER ON LAST PAGE ═══
  pdf.setFillColor(...NAVY)
  pdf.rect(0, PH - 16, PW, 16, 'F')
  pdf.setFontSize(6)
  pdf.setTextColor(255, 255, 255, 115)
  pdf.text(`© ${new Date().getFullYear()} Sidewalk Security Advisors. This document is confidential and proprietary.`, ML, PH - 8)
  pdf.text(`${dc.policy_id || ''} · v${dc.version || clientPolicy?.version_number || '1.0'} · End of Document`, PW - MR, PH - 8, { align: 'right' })

  // Save
  const today = new Date().toISOString().split('T')[0]
  const sn = (clientData?.name || 'SSA').replace(/[^a-zA-Z0-9]/g, '_')
  const st = (title || 'Policy').replace(/[^a-zA-Z0-9]/g, '_')
  const sv = clientPolicy?.policy_version_number || clientPolicy?.version_number || clientPolicy?.current_version || '1.0'
  pdf.save(`${sn}_${st}_v${sv}_${today}.pdf`)
}
